/**
 * The bridge and its two guns (GDD §3.6, §3.7, §4.5, §5, §5.2; concept L887–L895, L1060–L1109). A bridge takes
 * 6 + level hits. When it breaks, the two spans at the struck point fall and splash, their neighbours sag, the guns
 * explode, the level rises and the next section spawns. Until then its guns track the jet and fire from level 3.
 */
import { config } from '../core/config.ts';
import { clamp, sstep } from '../core/math.ts';
import type { GameSim } from '../sim/context.ts';
import { pushEvent } from '../sim/context.ts';
import type { BridgeSeg, BridgeState, Turret } from '../sim/state.ts';
import type { WorldProfile } from '../world/profiles.ts';
import { enemyFire } from './shots.ts';
import { addScore } from '../rules/scoring.ts';
import { beginNextSection } from '../rules/checkpoint.ts';

/** Bridge span = full river width + 88 m (GDD §3.6; concept L887). */
const ABUTMENT = 44;
/** Turret mount on its span (concept L890) and the muzzle height above it (L1106). */
const TURRET_Y = 0.1;
const TURRET_Z = 3.4;
const MUZZLE_Y = 1.3;
/** Falling spans: gravity, splash and vanish heights (concept L1091–L1093). */
const G_SPAN = 9.8;
const SPLASH_Y = 1.5;
const GONE_Y = -12;
/** Sagging neighbours: tilt and drop over 1.2 s (L1094). */
const SAG_TILT = 0.32;
const SAG_DROP = 2.2;
const SAG_TIME = 1.2;
/** Smoke over the broken bridge for 4 s (L1096). */
const SMOKE_SEC = 4;

export function createBridgeState(W: WorldProfile): BridgeState {
  const span = 2 * (W.width(W.bridgeZ) + ABUTMENT);
  const n = Math.max(7, Math.round(span / 12)); // concept L509 (src/entities/models/bridge.ts)
  const seg = span / n;
  const segs: BridgeSeg[] = [];
  for (let i = 0; i < n; i++) {
    const hx = -span / 2 + seg * (i + 0.5);
    segs.push({
      homeX: hx,
      homeY: W.deckY,
      homeZ: 0,
      x: hx,
      y: W.deckY,
      z: 0,
      rotX: 0,
      rotY: 0,
      rotZ: 0,
      visible: true,
      falling: false,
      vx: 0,
      vy: 0,
      vz: 0,
      wx: 0,
      wy: 0,
      wz: 0,
      sag: 0,
      splashed: false,
    });
  }
  const turrets: Turret[] = [1, n - 2].map((i) => ({ seg: i, visible: true, headY: 0, flash: false, cd: 1 }));
  return {
    rootX: W.center(W.bridgeZ),
    z: W.bridgeZ,
    deckY: W.deckY,
    span,
    seg,
    n,
    intact: true,
    engaged: false,
    hp: config.guns.bridgeHitsBase + config.guns.bridgeHitsPerLevel,
    since: 0,
    segs,
    turrets,
    fall: new Array<number>(4).fill(0),
    fallCount: 0,
  };
}

/** A turret's world position, as three.js composes it through bridge root → span → turret (concept L1101). */
export function turretWorld(b: BridgeState, t: Turret, out: [number, number, number]): [number, number, number] {
  const sg = b.segs[t.seg];
  if (!sg) return out;
  out[0] = sg.x + b.rootX;
  out[1] = TURRET_Y + sg.y;
  out[2] = TURRET_Z + (sg.z + b.z);
  return out;
}

/** concept resetBridge (L1078–L1083): intact again, 6 + level hits, guns back with a 1 s cooldown. */
export function resetBridge(sim: GameSim): void {
  const b = sim.bridge;
  for (let i = 0; i < b.segs.length; i++) {
    const sg = b.segs[i] as BridgeSeg;
    sg.x = sg.homeX;
    sg.y = sg.homeY;
    sg.z = sg.homeZ;
    sg.rotX = sg.rotY = sg.rotZ = 0;
    sg.visible = true;
    sg.falling = false;
    sg.vx = sg.vy = sg.vz = 0;
    sg.sag = 0;
    sg.splashed = false;
  }
  b.fallCount = 0;
  b.intact = true;
  b.engaged = false;
  b.hp = config.guns.bridgeHitsBase + config.guns.bridgeHitsPerLevel * sim.run.level;
  for (let i = 0; i < b.turrets.length; i++) {
    const t = b.turrets[i] as Turret;
    t.visible = true;
    t.cd = 1;
  }
}

/** concept hitBridge (L1060–L1065). */
export function hitBridge(sim: GameSim, x: number): void {
  const b = sim.bridge;
  if (!b.intact) return;
  b.hp--;
  sim.fx.bridgeHit(x, b.deckY + 1, b.z + 6);
  if (b.hp <= 0) destroyBridge(sim, x);
}

const tw: [number, number, number] = [0, 0, 0];

/** concept destroyBridge (L1066–L1077). Span velocities and spins come from the effects stream. */
export function destroyBridge(sim: GameSim, x: number): void {
  const b = sim.bridge;
  const rand = sim.streams.fx;
  const rx = b.rootX;
  b.intact = false;
  b.since = 0;
  for (let k = 0; k < b.segs.length; k++) (b.segs[k] as BridgeSeg).splashed = false;
  const i = clamp(Math.floor((x - rx + b.span / 2) / b.seg), 1, b.n - 3);
  b.fallCount = 0;
  for (let k = 0; k < 2; k++) {
    const sg = b.segs[i + k];
    if (!sg) continue;
    sg.falling = true;
    sg.vx = k ? 5 : -5;
    sg.vy = 6;
    sg.vz = (rand() - 0.5) * 3;
    sg.wx = (rand() - 0.5) * 1.2;
    sg.wy = (rand() - 0.5) * 0.6;
    sg.wz = k ? -1.4 : 1.4;
    b.fall[b.fallCount++] = i + k;
  }
  for (let k = 0; k < 2; k++) {
    const j = k ? i + 2 : i - 1;
    const sg = b.segs[j];
    if (sg && j > 0 && j < b.n - 1) {
      sg.sag = k ? -1 : 1;
      b.fall[b.fallCount++] = j;
    }
  }
  sim.fx.boom(rx - b.span / 2 + b.seg * (i + 1), b.deckY + 1, b.z, 2.2);
  for (let k = 0; k < b.turrets.length; k++) {
    const t = b.turrets[k] as Turret;
    if (!t.visible) continue;
    turretWorld(b, t, tw);
    sim.fx.boom(tw[0], tw[1] + 1, tw[2], 0.7);
    t.visible = false;
  }
  const run = sim.run;
  b.engaged = false;
  addScore(sim, config.score.bridge);
  // The title screen's demo stays on its level, so a player can take it over at any moment (judgment call 28).
  if (!run.demo) run.level++;
  run.skill = Math.max(0.45, 0.86 - run.level * 0.04);
  run.progressFrom = b.z;
  pushEvent(sim, 'bridgeDestroyed', run.level);
  pushEvent(sim, 'levelStart', run.level);
  beginNextSection(
    sim,
    b.z - config.spawning.startPastBridge,
    b.z - config.spawning.sectionLength + 40,
    b.z - config.lives.checkpointPastBridge,
  );
}

/** concept updBridge (L1084–L1109). */
export function stepBridge(sim: GameSim, dt: number): void {
  const b = sim.bridge;
  b.engaged = false;
  if (!b.intact) {
    b.since += dt;
    for (let f = 0; f < b.fallCount; f++) {
      const sg = b.segs[b.fall[f] ?? 0];
      if (!sg) continue;
      if (sg.falling) {
        sg.vy -= G_SPAN * dt;
        sg.x += sg.vx * dt;
        sg.y += sg.vy * dt;
        sg.z += sg.vz * dt;
        sg.rotX += sg.wx * dt;
        sg.rotY += sg.wy * dt;
        sg.rotZ += sg.wz * dt;
        if (sg.y < SPLASH_Y && !sg.splashed) {
          sg.splashed = true;
          sim.fx.spanSplash(sg.x + b.rootX, b.z);
        }
        if (sg.y < GONE_Y) {
          sg.vx = sg.vy = sg.vz = 0;
          sg.visible = false;
        }
      } else if (sg.sag) {
        const q = sstep(0, SAG_TIME, b.since);
        sg.rotZ = sg.sag * SAG_TILT * q;
        sg.y = sg.homeY - SAG_DROP * q;
      }
    }
    if (b.since < SMOKE_SEC) sim.fx.bridgeSmoke(b.rootX, b.deckY, b.z, dt);
    return;
  }
  const run = sim.run;
  const jet = sim.jet;
  const ahead = jet.z - b.z;
  const g = config.firing.bridgeGun;
  const cd = config.firing.bridgeCooldown;
  const quiet = sim.rules === 'gdd' && run.invuln > 0; // D7: no enemy fires while the jet is invulnerable
  // The guns are engaged exactly while they may fire (the HUD's alert, D18).
  b.engaged =
    run.level >= config.difficulty.bridgeGunsFromLevel &&
    run.alive &&
    run.title <= 0 &&
    !quiet &&
    ahead > g.range.min &&
    ahead < g.range.max;
  for (let k = 0; k < b.turrets.length; k++) {
    const t = b.turrets[k] as Turret;
    turretWorld(b, t, tw);
    t.headY = Math.atan2(-(jet.x - tw[0]), -(jet.z - tw[2]));
    t.flash = false;
    if (b.engaged) {
      t.cd -= dt;
      if (t.cd <= 0) {
        enemyFire(sim, tw[0], tw[1] + MUZZLE_Y, tw[2], 'tracer', 'bridgeGuns');
        t.flash = true;
        t.cd = cd.base + ahead / cd.distanceDivisor + sim.streams.combat() * cd.jitter;
      }
    }
  }
}
